import { type CupRound, isCupMatch } from './cupRounds';

/**
 * Regroupement « par tour » des écrans de planification : un match de championnat
 * se range dans sa journée (game_day), un match de coupe (isCupMatch) dans son tour
 * (cup_round) — jamais dans sa journée, qui vaut presque toujours 1. Spec :
 * docs/superpowers/specs/2026-10-16-coupe-algerie-cup-rounds-design.md §6.6 / §7.2.
 */

/** Journée de championnat, ou tour de coupe (null = tour non précisé). */
export type RoundKey = { kind: 'game_day'; gameDay: number } | { kind: 'cup'; round: string | null };

/** Ce qu'il faut d'un match pour le ranger (Match ou forme partielle). */
export interface RoundKeyMatch {
  isExternalCompetition?: boolean | null;
  leagueGroup?: { kind?: string | null } | null;
  cupRound?: string | null;
  gameDay?: number | null;
}

export function roundKeyOf(m: RoundKeyMatch): RoundKey {
  if (isCupMatch(m)) return { kind: 'cup', round: m.cupRound || null };
  return { kind: 'game_day', gameDay: typeof m.gameDay === 'number' && !Number.isNaN(m.gameDay) ? m.gameDay : 0 };
}

/** Identifiant stable d'un tas : 'gd:4', 'cup:semi_final', 'cup:none'. */
export function roundKeyId(k: RoundKey): string {
  return k.kind === 'game_day' ? `gd:${k.gameDay}` : `cup:${k.round ?? 'none'}`;
}

/**
 * Journées croissantes, puis tours de coupe par sort_order, puis tours absents de la
 * table (par code), puis « tour non précisé ».
 */
export function compareRoundKeys(a: RoundKey, b: RoundKey, rounds: ReadonlyMap<string, CupRound>): number {
  if (a.kind === 'game_day' && b.kind === 'game_day') return a.gameDay - b.gameDay;
  if (a.kind === 'game_day') return -1;
  if (b.kind === 'game_day') return 1;
  const rank = (k: { round: string | null }) =>
    k.round === null ? 2 : rounds.has(k.round) ? 0 : 1;
  const ra = rank(a);
  const rb = rank(b);
  if (ra !== rb) return ra - rb;
  if (ra === 0) return rounds.get(a.round!)!.sortOrder - rounds.get(b.round!)!.sortOrder;
  if (ra === 1) return a.round!.localeCompare(b.round!);
  return 0;
}

/**
 * Valeur d'un tas dans un filtre, un paramètre d'URL ou une clé d'idempotence :
 * la journée nue pour un match de championnat (format historique, inchangé :
 * « 4 »), 'cup:<code>' / 'cup:none' pour un match de coupe.
 */
export function roundParamValue(k: RoundKey): string {
  return k.kind === 'game_day' ? String(k.gameDay) : roundKeyId(k);
}

/** Inverse de roundParamValue ; null pour '', 'all' ou une valeur illisible. */
export function parseRoundParam(value: string | null | undefined): RoundKey | null {
  if (!value) return null;
  if (value.startsWith('cup:')) {
    const code = value.slice(4);
    if (!code) return null;
    return { kind: 'cup', round: code === 'none' ? null : code };
  }
  if (!/^-?\d+$/.test(value)) return null;
  return { kind: 'game_day', gameDay: Number(value) };
}

/** Le match tombe-t-il dans le tas choisi par un filtre (valeur roundParamValue) ? */
export function matchesRoundParam(m: RoundKeyMatch, value: string): boolean {
  return roundParamValue(roundKeyOf(m)) === value;
}

/** Les tas présents dans une liste de matchs, une fois chacun, triés. */
export function distinctRoundKeys(
  matches: Iterable<RoundKeyMatch>,
  rounds: ReadonlyMap<string, CupRound>,
): RoundKey[] {
  const byId = new Map<string, RoundKey>();
  for (const m of matches) {
    const k = roundKeyOf(m);
    const id = roundKeyId(k);
    if (!byId.has(id)) byId.set(id, k);
  }
  return [...byId.values()].sort((a, b) => compareRoundKeys(a, b, rounds));
}

/**
 * Matchs d'un groupe dans un tas (programmation groupée, onUpdateGameDaySchedule) :
 * par tour pour un match de coupe — jamais par game_day —, par journée sinon.
 *
 * `matches` peut porter plusieurs copies d'un même match (un cache de requête par
 * écran) et toutes ne lisent pas cup_round (profils disciplinary / rankings,
 * get_kanban_matches) ni league_groups.kind : une copie à cupRound null ne dit pas
 * que le match est sans tour. Les copies sont donc réunies par id avant de classer :
 *   - match de coupe si une copie au moins l'est (externe ou groupe kind 'cup') ;
 *   - tour : il tombe dans un tour si une de ses copies porte ce tour ; dans « tour
 *     non précisé » seulement si aucune copie n'en porte ;
 *   - championnat (inchangé) : une copie du groupe à cette journée suffit ; une
 *     journée absente (null) n'est jamais sélectionnée.
 * Une copie du groupe demandé est rendue (la dernière qui convient), un match par id.
 */
export function selectRoundMatches<M extends RoundKeyMatch & { id: string; leagueGroup?: { id?: string } | null }>(
  matches: Iterable<M>,
  leagueGroupId: string,
  round: RoundKey,
): M[] {
  const copiesById = new Map<string, M[]>();
  for (const m of matches) {
    const copies = copiesById.get(m.id);
    if (copies) copies.push(m);
    else copiesById.set(m.id, [m]);
  }

  const selected: M[] = [];
  for (const copies of copiesById.values()) {
    const inGroup = copies.filter((c) => c.leagueGroup?.id === leagueGroupId);
    if (inGroup.length === 0) continue;
    const isCup = copies.some((c) => isCupMatch(c));

    if (round.kind === 'game_day') {
      if (isCup) continue;
      const hits = inGroup.filter((c) => typeof c.gameDay === 'number' && c.gameDay === round.gameDay);
      if (hits.length > 0) selected.push(hits[hits.length - 1]);
      continue;
    }

    if (!isCup) continue;
    const withRound = inGroup.filter((c) => !!c.cupRound);
    if (round.round === null) {
      if (copies.some((c) => !!c.cupRound)) continue;
      selected.push(inGroup[inGroup.length - 1]);
    } else {
      const hits = withRound.filter((c) => c.cupRound === round.round);
      if (hits.length > 0) selected.push(hits[hits.length - 1]);
      else if (copies.some((c) => c.cupRound === round.round)) selected.push(inGroup[inGroup.length - 1]);
    }
  }
  return selected;
}

/**
 * Dernière journée de championnat de chaque groupe (bouton « Dernière journée » des
 * rapports de discipline) : les matchs de coupe n'y entrent pas — leur game_day ne dit
 * rien de l'avancement du championnat. Un groupe sans match de championnat est absent.
 */
export function lastGameDayByGroup(
    matches: Iterable<RoundKeyMatch & { leagueGroup?: { id?: string } | null }>,
): Map<string, number> {
    const res = new Map<string, number>();
    for (const m of matches) {
        const gid = m.leagueGroup?.id;
        if (!gid || isCupMatch(m) || typeof m.gameDay !== 'number' || Number.isNaN(m.gameDay)) continue;
        if (m.gameDay > (res.get(gid) ?? 0)) res.set(gid, m.gameDay);
    }
    return res;
}
